import { ValidationResult as EngineValidationResult } from "@imi-ernd-be/hyselect-engine";
import { z } from "zod/v4";
import { engine } from "..";
import { isPhysicalType, PhysicalType } from "../units/constants";

export type ValidationConstraints = ValidationResult["constraints"];
export type ValidationResult = z.infer<typeof validationResultSchema>;
type Code = ValidationResult["code"];
type Severity = ValidationResult["severity"];

export const validationResultSchema = z.object({
  code: z.enum([
    "custom",
    "excluded",
    "invalid",
    "noMatch",
    "outOfRange",
    "required",
    "tooHigh",
    "tooLow",
  ]),
  constraints: z.object({
    max: z.number().nullable(),
    min: z.number().nullable(),
    physicalType: z
      .string()
      .nullable()
      .transform((v) => v as PhysicalType | null),
    reference: z.string().nullable(),
    sizeMax: z.string().nullable(),
    sizeMin: z.string().nullable(),
    value: z.number(),
  }),
  path: z.string(),
  severity: z.enum(["error", "info", "warning"]),
  translationKey: z.string().nullable(),
});

export function buildValidationResult(
  result: EngineValidationResult | null,
): ValidationResult | null {
  if (!result || result.severity === "ok") return null;
  return {
    code: isErrorCode(result.code) ? result.code : "invalid",
    constraints: {
      max: result.max === engine.Constants.getDBLMax() ? null : result.max,
      min: result.min === engine.Constants.getDBLMin() ? null : result.min,
      physicalType: isPhysicalType(result.physicalType)
        ? result.physicalType
        : null,
      reference: result.reference,
      sizeMax: result.sizeMax === "" ? null : result.sizeMax,
      sizeMin: result.sizeMin === "" ? null : result.sizeMin,
      value: result.value,
    },
    path: result.path,
    severity: isErrorSeverity(result.severity) ? result.severity : "info",
    translationKey: result.translationKey || null,
  };
}

export function hasValidationResult(
  result: EngineValidationResult | null,
): result is EngineValidationResult {
  return !!result && !!result.code && !!result.path && !!result.severity;
}

export function isErrorSeverity(severity: string): severity is Severity {
  return ["error", "warning"].includes(severity);
}

function isErrorCode(code: string): code is Code {
  return [
    "custom",
    "excluded",
    "invalid",
    "noMatch",
    "outOfRange",
    "required",
    "tooHigh",
    "tooLow",
  ].includes(code);
}
